import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { GAME_QUERY_KEY } from './useGetGame';

export interface AdminSeatRefundLine {
    paymentIntentId: string;
    /** Whose card the money goes back to — the payer, not necessarily the player. */
    payerUserId: string;
    kind: 'full' | 'partial';
    amountCents: number;
}

export interface AdminRefundSeatResponse {
    gameId: number;
    userId: string;
    refunds: AdminSeatRefundLine[];
    skipped: Array<{ paymentIntentId: string; reason: string }>;
}

export interface AdminRefundSeatRequest {
    gameId: number;
    userId: string;
    /**
     * Display-only hints for the success toast (the API answers amounts in the
     * intent's currency and does not say which rail paid): a wallet-paid seat
     * goes back to the payer's wallet as credits, not to a card.
     */
    paidWithWallet?: boolean;
    currency?: string;
}

/** `12.50 EUR` — the currency code only when the caller knows it. */
export const formatRefundAmount = (cents: number, currency?: string): string => {
    const amount = (cents / 100).toFixed(2);
    return currency ? `${amount} ${currency.toUpperCase()}` : amount;
};

/**
 * Refund ONE former participant's seat — their own payment, and/or the share
 * somebody else paid for them on the pay-for-others flow (which goes back to
 * that payer's card). The counterpart of the kick, which refunds nothing.
 *
 * The API refuses while the player is still seated (`PARTICIPANT_STILL_SEATED`)
 * and reports what it left alone in `skipped` (already refunded, paid by cash
 * or POS, …) rather than failing, so a second click is harmless.
 */
export const useAdminRefundSeat = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ gameId, userId }: AdminRefundSeatRequest) => {
            return apiClient.fetchApi<AdminRefundSeatResponse, {}>({
                endpoint: `payments/admin/refund-seat/game/${gameId}/user/${encodeURIComponent(userId)}`,
                method: 'POST',
                body: {},
            });
        },
        onSuccess: (data, { gameId, paidWithWallet, currency }) => {
            const totalCents = data.refunds.reduce((sum, r) => sum + r.amountCents, 0);
            if (data.refunds.length > 0) {
                toast({
                    title: translate('game.refund_seat_success'),
                    description: translate(
                        paidWithWallet
                            ? 'game.refund_seat_success_description_wallet'
                            : 'game.refund_seat_success_description',
                        {
                            amount: formatRefundAmount(totalCents, currency),
                            count: data.refunds.length,
                        },
                    ),
                    status: 'success',
                    duration: 6000,
                    isClosable: true,
                });
            } else {
                // Nothing queued: every intent was skipped. Say why, in the
                // API's words, instead of a green toast for a no-op.
                toast({
                    title: translate('game.refund_seat_nothing'),
                    description: data.skipped.map((s) => s.reason).join(' · ') || undefined,
                    status: 'info',
                    duration: 8000,
                    isClosable: true,
                });
            }
            queryClient.invalidateQueries({ queryKey: GAME_QUERY_KEY(gameId) });
        },
        onError: (error: any) => {
            toast({
                title: translate('game.refund_seat_error'),
                description: error?.message || translate('common.unexpected_error'),
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
        },
    });
};
